import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Printer, RefreshCw, ArrowLeft, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FIRM } from "@/lib/firm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORT_CSS = `
  .cr-wrap{background:#0b0f1a;min-height:100vh;padding:28px 16px;}
  .cr-sheet{background:#fff;color:#141410;max-width:8.5in;margin:0 auto;padding:0.6in 0.7in;box-shadow:0 10px 40px rgba(0,0,0,.45);font-family:'Times New Roman',Georgia,serif;font-size:11pt;}
  .cr-head{text-align:center;border-bottom:2px solid #14204a;padding-bottom:12px;margin-bottom:6px;}
  .cr-firm{font-size:16pt;font-weight:700;letter-spacing:.04em;color:#14204a;text-transform:uppercase;}
  .cr-firmsub{font-size:8.5pt;color:#444;margin-top:3px;}
  .cr-title{font-size:13pt;font-weight:700;text-transform:uppercase;letter-spacing:.06em;margin:14px 0 2px;color:#14204a;}
  .cr-meta{font-size:8.5pt;color:#666;margin-bottom:10px;}
  .cr-cat{font-size:10.5pt;font-weight:700;color:#14204a;text-transform:uppercase;letter-spacing:.04em;margin:16px 0 4px;border-bottom:1px solid #b9c2d6;padding-bottom:2px;}
  .cr-table{width:100%;border-collapse:collapse;font-size:9.5pt;}
  .cr-table th,.cr-table td{border:1px solid #b9c2d6;padding:4px 7px;text-align:left;vertical-align:top;}
  .cr-table th{background:#eef1f8;color:#14204a;font-weight:700;text-transform:uppercase;font-size:8.5pt;letter-spacing:.03em;}
  .cr-table td.cr-name{font-weight:600;}
  .cr-mono{font-family:'Courier New',monospace;font-size:9pt;white-space:nowrap;}
  .cr-dash{color:#999;}
  .cr-foot{margin-top:22px;border-top:1px solid #b9c2d6;padding-top:8px;font-size:8pt;color:#666;text-align:center;}
  @media print{
    .cr-wrap{background:#fff!important;padding:0!important;}
    .cr-sheet{box-shadow:none!important;margin:0!important;max-width:none!important;padding:0.4in!important;}
    .no-print{display:none!important;}
    .cr-cat{break-after:avoid;}
    tr{break-inside:avoid;}
    @page{size:letter;margin:0.4in;}
  }
`;

const D = ({ v }) => (v && String(v).trim() ? <span>{v}</span> : <span className="cr-dash">—</span>);

export default function CounterpartyReport() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const token = localStorage.getItem("th_admin_token");
      const r = await axios.get(`${API}/admin/counterparty-report`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load the counterparty report.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const generated = data?.generated_at ? new Date(data.generated_at).toLocaleString() : new Date().toLocaleString();

  return (
    <div className="cr-wrap" data-testid="counterparty-report-page">
      <style>{REPORT_CSS}</style>

      <div className="no-print" style={{ maxWidth: "8.5in", margin: "0 auto 14px", display: "flex", gap: 10, justifyContent: "space-between" }}>
        <button data-testid="cr-back-btn" onClick={() => nav(-1)}
          style={btn("#1f2a44")}><ArrowLeft size={15} /> Back</button>
        <div style={{ display: "flex", gap: 10 }}>
          <button data-testid="cr-refresh-btn" onClick={load}
            style={btn("#1f2a44")}><RefreshCw size={15} /> Refresh</button>
          <button data-testid="cr-print-btn" onClick={() => window.print()} disabled={loading || !data}
            style={btn("#B8892B")}><Printer size={15} /> Print Report</button>
        </div>
      </div>

      <div className="cr-sheet">
        <div className="cr-head">
          <div className="cr-firm">{FIRM.name}</div>
          <div className="cr-firmsub">EIN {FIRM.ein} · {FIRM.address} · {FIRM.phone}</div>
        </div>
        <div className="cr-title">Counterparty / Lender / Servicer Directory</div>
        <div className="cr-meta">
          Combined address book — fax rolodex &amp; deal lenders/servicers · Generated {generated}
          {data ? ` · ${data.total} ${data.total === 1 ? "entry" : "entries"}` : ""}
        </div>

        {loading && (
          <p data-testid="cr-loading" style={{ color: "#666", display: "flex", alignItems: "center", gap: 8 }}>
            <Loader2 className="animate-spin" size={16} /> Loading directory…
          </p>
        )}
        {err && !loading && <p data-testid="cr-error" style={{ color: "#7a1f24" }}>{err}</p>}
        {!loading && !err && data && data.total === 0 && (
          <p data-testid="cr-empty" style={{ color: "#666" }}>No counterparties on file yet. Add lenders/servicers in the Fax rolodex or on a borrower deal.</p>
        )}

        {!loading && !err && data && data.groups.map((g) => (
          <div key={g.category} data-testid={`cr-group-${g.category}`}>
            <div className="cr-cat">{g.category}</div>
            <table className="cr-table">
              <thead>
                <tr>
                  <th style={{ width: "18%" }}>Borrower</th>
                  <th style={{ width: "24%" }}>Lender / Servicer</th>
                  <th style={{ width: "30%" }}>Mailing Address</th>
                  <th style={{ width: "14%" }}>Fax</th>
                  <th style={{ width: "14%" }}>Phone</th>
                </tr>
              </thead>
              <tbody>
                {g.entries.map((e, i) => (
                  <tr key={i} data-testid="cr-row">
                    <td><D v={e.borrower} /></td>
                    <td className="cr-name">{e.name}</td>
                    <td><D v={e.address} /></td>
                    <td className="cr-mono"><D v={e.fax} /></td>
                    <td className="cr-mono"><D v={e.phone} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        <div className="cr-foot">
          {FIRM.name} · Confidential counterparty directory. Fax numbers vary by department — verify before sending.
        </div>
      </div>
    </div>
  );
}

const btn = (bg) => ({
  display: "flex", alignItems: "center", gap: 7, background: bg, color: "#fff",
  border: "none", borderRadius: 6, padding: "9px 16px", fontWeight: 700,
  fontSize: "12px", letterSpacing: ".04em", textTransform: "uppercase", cursor: "pointer",
});
